import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../api";
import SimpleCombobox from "../../../components/SimpleCombobox";
import { debounce } from "lodash";
import FormNewPatient from "../../../components/FormNewPatient";
import MyDatePicker from "./DatePicker";
import { ExclamationCircleIcon, PlusIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import TextEditor from "../../../components/TextEditor";
import { getLocalDateString } from "../../../util/formatDate";

export const AppointmentForm = ({
    dialogRef,
    initialAppointment,
    selectedDoctor,
    onSave,
    notify,
}) => {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [doctors, setDoctors] = useState([]);
    const [appointment, setAppointment] = useState({
        patient: null,
        doctor: null,
        unscheduled: false,
        contactLens: false,
        dilatate: false,
        appointmentType: 1,
        date: "",
        time: "",
        notes: "",
    });
    const [formErrors, setFormErrors] = useState({
        id: false,
        patient: false,
        doctor: false,
        unscheduled: false,
        date: false,
        time: false,
        notes: false,
    });
    const [scheduleDates, setScheduleDates] = useState([]);
    const [scheduleTimes, setScheduleTimes] = useState([]);
    const [cancellationAlert, setCancellationAlert] = useState(null);
    const [restrictionWarning, setRestrictionWarning] = useState(null);
    const [restrictionInfo, setRestrictionInfo] = useState(null);
    const patientDialogRef = useRef(null);

    useEffect(() => {
        if (initialAppointment) {
            setAppointment({
                patient: initialAppointment.patient
                    ? {
                          ...initialAppointment.patient,
                          name: `${
                              initialAppointment.patient.firstName || ""
                          } ${initialAppointment.patient.lastName || ""} ${
                              initialAppointment.patient.govId || ""
                          }`.trim(),
                      }
                    : null,
                doctor: {
                    ...initialAppointment.doctor,
                    name: `${initialAppointment.doctor.firstName} ${initialAppointment.doctor.lastName} ${initialAppointment.doctor.govId}`,
                },
                unscheduled: initialAppointment.unscheduled || false,
                dilatate: initialAppointment.dilatate || false,
                contactLens: initialAppointment.contactLens || false,
                date: initialAppointment.date || "",
                time: initialAppointment.time || "",
                notes: initialAppointment.notes || "",
                appointmentType: initialAppointment.appointmentType,
                id: initialAppointment.id || null,
            });
            if (initialAppointment.doctor.id) {
                const payload = {
                    doctorId: initialAppointment.doctor.id,
                };
                api.availability.create(payload).then((response) => {
                    setScheduleDates(response);
                });
            }
            if (initialAppointment.date) {
                const payload = {
                    doctorId: initialAppointment.doctor.id,
                    date: initialAppointment.date,
                };
                api.availability.create(payload).then((response) => {
                    setScheduleTimes(response);
                });
            }
        } else if (selectedDoctor) {
            setAppointment({
                ...appointment,
                doctor: {
                    ...selectedDoctor,
                    name: `${selectedDoctor.firstName} ${selectedDoctor.lastName} ${selectedDoctor.govId}`,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialAppointment, selectedDoctor]);

    useEffect(() => {
        api.patients
            .getMulti("firstName", null, null, null, null)
            .then((response) => {
                const { results } = response;
                const patientsOptions = results.map((person) => ({
                    ...person,
                    name: `${person.firstName} ${person.lastName} ${person.govId}`,
                }));
                setPatients(patientsOptions);
            });
    }, []);

    useEffect(() => {
        api.doctors.getMulti().then((response) => {
            const data = response;
            const doctorsOptions = data.map((doc) => ({
                ...doc,
                name: `${doc.firstName} ${doc.lastName} ${doc.govId}`,
            }));
            setDoctors(doctorsOptions);
        });
    }, []);

    const handlePatientChange = (field, value) => {
        setAppointment((prev) => ({
            ...prev,
            [field]: value,
        }));
        setFormErrors({ ...formErrors, [field]: false });
        
        // Verificar historial de cancelaciones cuando se selecciona un paciente
        if (value && value.id && value.cancellationAlert) {
            if (value.cancellationAlert.hasAlert) {
                setCancellationAlert(value.cancellationAlert);
            } else {
                setCancellationAlert(null);
            }
        } else {
            setCancellationAlert(null);
        }
    };

    const handleChange = (field, value) => {
        setAppointment((prev) => ({
            ...prev,
            [field]:
                typeof value === "object" && value !== null ? value.id : value,
        }));
        setFormErrors({ ...formErrors, [field]: false });
    };

    useEffect(() => {
        if (appointment.patient && appointment.doctor) {
            api.patientDoctorRestrictions
                .getMulti(null, null, null, null, {
                    patient_id: appointment.patient.id,
                    doctor_id: appointment.doctor.id,
                    is_active: true,
                })
                .then((response) => {
                    const restrictions = response.results || response;
                    const activeRestriction = restrictions.find((r) => {
                        if (r.expiresAt) {
                            return new Date(r.expiresAt) > new Date();
                        }
                        return true;
                    });

                    if (activeRestriction) {
                        if (activeRestriction.severity === 2) {
                            setRestrictionWarning(activeRestriction);
                            setRestrictionInfo(null);
                        } else if (activeRestriction.severity === 3) {
                            setRestrictionInfo(activeRestriction);
                            setRestrictionWarning(null);
                        } else {
                            setRestrictionWarning(null);
                            setRestrictionInfo(null);
                        }
                    } else {
                        setRestrictionWarning(null);
                        setRestrictionInfo(null);
                    }
                })
                .catch(() => {
                    setRestrictionWarning(null);
                    setRestrictionInfo(null);
                });
        } else {
            setRestrictionWarning(null);
            setRestrictionInfo(null);
        }
    }, [appointment.patient, appointment.doctor]);

    const handleDoctorChange = (field, value) => {
        setScheduleDates([]);
        setScheduleTimes([]);
        if (value) {
            const payload = {
                doctorId: value.id,
                unscheduled: appointment.unscheduled
                    ? appointment.unscheduled
                    : false,
            };
            api.availability.create(payload).then((response) => {
                setScheduleDates(response);
            });
        }
        setAppointment((prev) => ({
            ...prev,
            [field]: value,
        }));
        setFormErrors({ ...formErrors, [field]: false });
    };

    const handleDateChange = (field, value) => {
        setScheduleTimes([]);
        if (appointment.doctor) {
            const payload = {
                doctorId: appointment.doctor.id,
                date: value,
            };
            api.availability.create(payload).then((response) => {
                setScheduleTimes(response);
            });
        }
        setAppointment((prev) => ({
            ...prev,
            [field]:
                typeof value === "object" && value !== null ? value.id : value,
        }));
        setFormErrors({ ...formErrors, [field]: false });
    };

    const handleChangeUnscheduled = (field, value) => {
        if (appointment.doctor) {
            const payload = {
                doctorId: appointment.doctor.id,
                unscheduled: value,
            };
            api.availability.create(payload).then((response) => {
                setScheduleDates(response);
            });
        }
        setAppointment((prev) => ({
            ...prev,
            time: null,
            [field]: value,
        }));
        setFormErrors({ ...formErrors, [field]: false });
    };

    const handleChangeCheck = (field, value) => {
        setAppointment((prev) => ({
            ...prev,
            [field]: value,
        }));
        setFormErrors({ ...formErrors, [field]: false });
    };

    const handleError = (field, value) =>
        setFormErrors((prev) => ({ ...prev, [field]: value }));

    const validateAppointment = () => {
        let error = false;

        if (!appointment.doctor) {
            handleError("doctor", "Este campo es obligatorio");
            error = true;
        }
        if (!appointment.patient) {
            handleError("patient", "Este campo es obligatorio");
            error = true;
        }
        if (!appointment.date) {
            handleError("date", "Este campo es obligatorio");
            error = true;
        }
        if (!appointment.time && !appointment.unscheduled) {
            handleError("time", "Este campo es obligatorio");
            error = true;
        }
        return error;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleQueryChange = useCallback(
        debounce(async (text) => {
            api.patients
                .getMulti("firstName", text, null, null, null)
                .then((response) => {
                    const { results } = response;
                    const patientsOptions = results.map((person) => ({
                        ...person,
                        name: `${person.firstName} ${person.lastName} ${person.govId}`,
                    }));
                    setPatients(patientsOptions);
                });
        }, 300),
        [],
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        if (validateAppointment()) return;
        const payload = {
            ...appointment,
            patient: appointment.patient?.id || null,
            doctor: appointment.doctor?.id || null,
            time: appointment.time === "" ? null : appointment.time,
        };
        try {
            if (appointment.id) {
                await api.appointments.update(appointment.id, payload);
                notify("Cita actualizada correctamente", "success");
            } else {
                await api.appointments.create(payload);
                notify("Cita creada correctamente", "success");
            }
            onSave?.();
            setAppointment({
                patient: null,
                doctor: null,
                unscheduled: false,
                date: "",
                time: "",
                notes: "",
            });
            setLoading(false);
            dialogRef.current?.close();
        } catch (error) {
            console.error("Error al guardar la cita:", error);
            if (error?.message && typeof error.message === "object") {
                const messages = Object.values(error.message).flat().join("\n");
                notify(messages, "error");
            } else {
                notify(
                    "Hubo un error al guardar la cita. Inténtalo nuevamente",
                    "error",
                );
            }
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-900 text-black dark:text-white">
            <div className="text-white bg-[#8098F9] dark:bg-[#4f5ea5]  p-5">
                <h2 className="text-3xl font-bold">Nuevo agendamiento</h2>
                <small className="text-[#C7D7FE]">
                    Empezá completando los datos a continuación para agendar tu
                    consulta médica.
                </small>
            </div>
            {appointment?.patient &&
                (!/^\d+$/.test(appointment.patient.govId) ||
                    !appointment.patient.govId) && (
                    <button
                        onClick={() => patientDialogRef.current?.showModal()}
                        className="flex  gap-1 w-full justify-center items-center px-10 py-5 bg-yellow-100 text-yellow-800"
                    >
                        <ExclamationCircleIcon className="h-6 w-6" />
                        <p>Actualice el número de cédula del paciente</p>
                    </button>
                )}
            <form
                onSubmit={handleSubmit}
                className=" text-gray-800 dark:text-gray-200 flex flex-col justify-between h-full"
            >
                <div className="p-5 flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2 items-end">
                            <div className="w-full">
                                <SimpleCombobox
                                    inputClassName=" relative mt-1 input-base"
                                    placeholder={"Buscar"}
                                    onQueryChange={handleQueryChange}
                                    handleChange={handlePatientChange}
                                    value={appointment.patient}
                                    name="patient"
                                    label="Paciente"
                                    labelClassname="font-semibold"
                                    options={patients}
                                    error={formErrors.patient}
                                />
                            </div>
                            <button
                                onClick={() => {
                                    patientDialogRef.current?.showModal?.();
                                }}
                                type="button"
                                disabled={appointment.patient}
                                className="bg-[#465FFF] p-2 rounded-lg text-white disabled:bg-gray-400"
                            >
                                <PlusIcon className="h-6 w-6" />
                            </button>
                        </div>
                        {cancellationAlert && (
                            <div className="flex gap-2 items-start p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                                        Alerta de cancelaciones
                                    </p>
                                    <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                                        {cancellationAlert.message}
                                    </p>
                                </div>
                            </div>
                        )}
                        {restrictionWarning && (
                            <div className="flex gap-2 items-start p-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 rounded-lg">
                                <span className="text-2xl flex-shrink-0">⚠️</span>
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                                        Advertencia
                                    </p>
                                    <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                                       {restrictionWarning.reason}
                                    </p>
                                    {restrictionWarning.notes && (
                                        <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-1">
                                            {restrictionWarning.notes}
                                        </p>
                                    )}
                                    {restrictionWarning.createdBy && (
                                        <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-2">
                                            Creada por: {restrictionWarning.createdBy.firstName} {restrictionWarning.createdBy.lastName}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {restrictionInfo && (
                            <div className="flex gap-2 items-start p-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-600 rounded-lg">
                                <span className="text-2xl flex-shrink-0">ℹ️</span>
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                                        Información
                                    </p>
                                    <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                                        {restrictionInfo.reason}
                                    </p>
                                    {restrictionInfo.notes && (
                                        <p className="text-sm text-blue-600 dark:text-blue-500 mt-1">
                                            {restrictionInfo.notes}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {appointment?.patient?.confirmedData === false && (
                            <p className="text-red-600">
                                Por favor{" "}
                                <button
                                    className="font-bold underline"
                                    type="button"
                                    onClick={() =>
                                        patientDialogRef.current?.showModal()
                                    }
                                >
                                    valide
                                </button>{" "}
                                los datos del paciente antes de continuar.
                            </p>
                        )}
                    </div>
                    <div className="flex justify-center items-start gap-4 ">
                        <input
                            type="checkbox"
                            name="unscheduled"
                            id="unscheduled"
                            className="mt-1"
                            onChange={(e) =>
                                handleChangeUnscheduled(
                                    "unscheduled",
                                    e.target.checked,
                                )
                            }
                            checked={appointment.unscheduled}
                            disabled={
                                appointment.patient &&
                                !appointment.patient.confirmedData
                            }
                        />
                        <div>
                            <label
                                htmlFor="unscheduled"
                                className="font-semibold"
                            >
                                Sin agendamiento
                            </label>
                            <p className="text-gray-500 dark:text-gray-400">
                                Marcá esta opción cuando el paciente llega sin
                                un agendamiento previo.
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-start gap-8 items-start ">
                        <div className="flex justify-center items-center gap-4">
                            <input
                                type="checkbox"
                                name="contactLens,"
                                id="contactLens"
                                className="mt-1"
                                onChange={(e) =>
                                    handleChangeCheck(
                                        "contactLens",
                                        e.target.checked,
                                    )
                                }
                                checked={appointment.contactLens}
                                disabled={
                                    appointment.patient &&
                                    !appointment.patient.confirmedData
                                }
                            />
                            <div>
                                <label
                                    htmlFor="contactLens"
                                    className="font-semibold"
                                >
                                    Lentes de contacto
                                </label>
                            </div>
                        </div>
                        <div className="flex justify-center items-center gap-4">
                            <input
                                type="checkbox"
                                name="dilatate"
                                id="dilatate"
                                className="mt-1"
                                onChange={(e) =>
                                    handleChangeCheck(
                                        "dilatate",
                                        e.target.checked,
                                    )
                                }
                                checked={appointment.dilatate}
                                disabled={
                                    appointment.patient &&
                                    !appointment.patient.confirmedData
                                }
                            />
                            <div>
                                <label
                                    htmlFor="dilatate"
                                    className="font-semibold"
                                >
                                    Dilatar
                                </label>
                            </div>
                        </div>
                    </div>
                    <div>
                        <fieldset>
                            <legend className="text-gray-700 dark:text-gray-200 font-semibold">
                                Tipo de cita
                            </legend>
                            <div className="mt-2 flex gap-6">
                                <div>
                                    <input
                                        type="radio"
                                        name="appointmentType"
                                        id="consulta"
                                        value={1}
                                        checked={
                                            appointment.appointmentType === 1
                                        }
                                        onChange={(e) =>
                                            handleChange(
                                                "appointmentType",
                                                parseInt(e.target.value),
                                            )
                                        }
                                    />
                                    <label htmlFor="consulta" className="ml-2">
                                        Consulta
                                    </label>
                                </div>
                                <div>
                                    <input
                                        type="radio"
                                        name="appointmentType"
                                        id="estudio"
                                        value={2}
                                        checked={
                                            appointment.appointmentType === 2
                                        }
                                        onChange={(e) =>
                                            handleChange(
                                                "appointmentType",
                                                parseInt(e.target.value),
                                            )
                                        }
                                    />
                                    <label htmlFor="estudio" className="ml-2">
                                        Estudio
                                    </label>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                    <div className="flex flex-col gap-2">
                        <SimpleCombobox
                            inputClassName="relative mt-1"
                            placeholder={"Buscar"}
                            handleChange={handleDoctorChange}
                            value={appointment.doctor}
                            name="doctor"
                            label="Doctor"
                            labelClassname="font-semibold"
                            options={doctors}
                            error={formErrors.doctor}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label htmlFor="date" className="font-semibold">
                            Fecha
                        </label>
                        <MyDatePicker
                            scheduleDates={scheduleDates}
                            appointment={appointment}
                            setAppointment={setAppointment}
                            handleDateChange={handleDateChange}
                            disabled={!appointment?.doctor}
                        />
                        <p className="mt-2 text-sm text-red-600">
                            {formErrors.date}
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <label htmlFor="time" className="font-semibold">
                            Hora
                        </label>
                        {appointment?.unscheduled ? (
                            <div>
                                <input
                                    type="time"
                                    onChange={(e) =>
                                        handleChange("time", e.target.value)
                                    }
                                    disabled={
                                        !appointment?.date
                                    }
                                    className="input-base"
                                    name="time"
                                    id="time"
                                    value={appointment.time}
                                />
                            </div>
                        ) : (
                            <select
                                className="input-base"
                                name="time"
                                id="time"
                                onChange={(e) =>
                                    handleChange("time", e.target.value)
                                }
                                disabled={
                                    !appointment?.date ||
                                    appointment?.unscheduled
                                }
                                value={appointment.time}
                            >
                                <option value="" disabled selected hidden>
                                    Seleccione un horario
                                </option>
                                {appointment.time && (
                                    <option value={appointment.time}>
                                        {appointment.time}
                                    </option>
                                )}
                                {scheduleTimes.length > 0 &&
                                    scheduleTimes.map((time, indx) => (
                                        <option value={time}>{time}</option>
                                    ))}
                            </select>
                        )}
                        <p className="mt-2 text-sm text-red-600">
                            {formErrors.time}
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <TextEditor
                            required={true}
                            value={appointment.notes}
                            handleChange={handleChange}
                            containerClassName="pb-8"
                            name="notes"
                            label="Notas"
                        />
                    </div>
                    {appointment?.patient?.managementPlans?.length > 0 && (
                        <div className="mb-4">
                            <TextEditor
                                name="managementPlans"
                                label="Ultima conducta:"
                                value={`${appointment.patient.managementPlans[0].date}: ${appointment.patient.managementPlans[0].description}`}
                                handleChange={() => {}}
                                error={null}
                                required={false}
                                readOnly={true}
                            />
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
                    <button
                        type="button"
                        className="text-gray-500 dark:text-gray-200 "
                        onClick={() => {
                            setAppointment({
                                patient: null,
                                doctor: null,
                                unscheduled: false,
                                date: "",
                                time: "",
                                notes: "",
                            });
                            dialogRef.current?.close();
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        disabled={
                            (!appointment?.time && !appointment?.unscheduled) ||
                            !appointment?.patient?.confirmedData ||
                            loading
                        }
                        type="submit"
                        className="bg-[#465FFF] text-white px-4 py-2 rounded-lg disabled:cursor-not-allowed disabled:bg-gray-500 disabled:text-gray-200 "
                    >
                        {loading ? (
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            "Guardar"
                        )}
                    </button>
                </div>
            </form>
            <dialog
                ref={patientDialogRef}
                closedBy="any"
                key={appointment?.patient?.id || "new-patient"}
            >
                <PersonalDataDialog
                    selectedPatient={appointment.patient}
                    setAppointment={setAppointment}
                    patientDialogRef={patientDialogRef}
                    notify={notify}
                />
            </dialog>
        </div>
    );
};

const PersonalDataDialog = ({
    selectedPatient,
    setAppointment,
    patientDialogRef,
    notify,
}) => {
    const [patient, setPatient] = useState({
        phoneNumbers: [],
        dm: false,
        hta: false,
        aler: false,
        gl: false,
        strabismus: false,
        glaucoma: false,
        cataractSurgery: false,
        glaucomaSurgery: false,
        refractiveSurgery: false,
        motherDm: false,
        motherHta: false,
        motherGlaucoma: false,
        fatherDm: false,
        fatherHta: false,
        fatherGlaucoma: false,
        siblingsDm: false,
        siblingsHta: false,
        siblingsGlaucoma: false,
        otherDm: false,
        otherHta: false,
        otherGlaucoma: false,
        registrationDate: getLocalDateString(),
    });
    const [error, setError] = useState();
    const radio = [
        { id: "male", default: true, label: "Masculino", value: "1" },
        { id: "female", default: false, label: "Femenino", value: "2" },
    ];
    const [cities, setCities] = useState([]);
    const [insurances, setInsurances] = useState([]);
    const [tab, setTab] = useState("DATOS PERSONALES");
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    useEffect(() => {
        api.insurances.getMulti().then((res) => {
            setInsurances(res);
            api.cities.getMulti().then((response) => {
                setCities(response);
                if (selectedPatient) {
                    api.patients.getOne(selectedPatient.id).then((data) => {
                        const insuranceIds = data.insurances || [];
                        setPatient({
                            ...data,
                            city: response.find(
                                (element) => element.id === data.city,
                            ),
                            insurances: res.filter((el) =>
                                insuranceIds.includes(el.id),
                            ),
                        });
                    });
                } else {
                    setPatient({
                        phoneNumbers: [],
                        dm: false,
                        hta: false,
                        aler: false,
                        gl: false,
                        strabismus: false,
                        glaucoma: false,
                        cataractSurgery: false,
                        glaucomaSurgery: false,
                        refractiveSurgery: false,
                        motherDm: false,
                        motherHta: false,
                        motherGlaucoma: false,
                        fatherDm: false,
                        fatherHta: false,
                        fatherGlaucoma: false,
                        siblingsDm: false,
                        siblingsHta: false,
                        siblingsGlaucoma: false,
                        otherDm: false,
                        otherHta: false,
                        otherGlaucoma: false,
                        registrationDate: getLocalDateString(),
                        city: response.find(
                            (element) => element.name === "Asuncion",
                        ),
                    });
                }
            });
        });
    }, [selectedPatient]);

    const handlePatientChange = (field, value) =>
        setPatient((prev) => ({ ...prev, [field]: value }));

    const handleError = (field, value) =>
        setError((prev) => ({ ...prev, [field]: value }));

    const handlePhones = (field, value) => {
        let obj = patient.phoneNumbers ? patient.phoneNumbers.slice() : [];
        obj.push({ number: value });
        setPatient((prev) => ({ ...prev, phoneNumbers: obj }));
    };

    const removePhone = (indx) => {
        let auxPhones = patient.phoneNumbers.slice(0);
        auxPhones.splice(indx, 1);
        setPatient((prev) => ({ ...prev, phoneNumbers: auxPhones }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setLoadingSubmit(true);
        const insurancesIds = (patient.insurances || [])
            .map((d) => (typeof d === "object" && d !== null ? d.id : d))
            .filter((id) => id !== null && id !== undefined);
        const payload = {
            ...patient,
            confirmedData: true,
            city:
                typeof patient.city === "object"
                    ? patient.city.id
                    : patient.city,
            insurances: insurancesIds,
        };

        const request = selectedPatient?.id
            ? api.patients.update(selectedPatient.id, payload)
            : api.patients.create(payload);

        const notifyMessage = selectedPatient?.id
            ? "Paciente actualizado correctamente"
            : "Paciente creado correctamente";

        request
            .then((response) => {
                setLoadingSubmit(false);
                setAppointment((prev) => ({
                    ...prev,
                    patient: {
                        ...response,
                        name: `${response.firstName} ${response.lastName} ${response.govId}`,
                        confirmedData: true,
                    },
                }));
                setTab("DATOS PERSONALES");
                notify(notifyMessage, "success");
                patientDialogRef.current.close();
            })
            .catch((error) => {
                console.log("error", error.message);
                setError(error.message);
                setLoadingSubmit(false);
                setTab("DATOS PERSONALES");
            });
    };

    return (
        <div>
            <FormNewPatient
                patient={patient}
                handleChange={handlePatientChange}
                handleError={handleError}
                error={error}
                radio={radio}
                cities={cities}
                insurances={insurances}
                handlePhones={handlePhones}
                removePhone={removePhone}
                onModal={true}
                tab={tab}
                setTab={setTab}
                loadingSubmit={loadingSubmit}
                handleSubmit={handleSubmit}
                setInsurances={setInsurances}
            />
        </div>
    );
};
